import { ChangeDetectorRef, Component, EventEmitter, forwardRef, Input, NgZone, OnChanges, OnInit, Output, ViewEncapsulation } from '@angular/core';
import { ControlValueAccessor, NG_VALIDATORS, NG_VALUE_ACCESSOR, UntypedFormBuilder, ValidationErrors } from '@angular/forms';
import { AppService, CoreProxyService, CoreTypes, LabelValue } from 'common-cloudstudio';
import { Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

@Component({
  selector: 'app-incident-general-info',
  templateUrl: './incident-general-info.component.html',
  styleUrls: ['./incident-general-info.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => IncidentGeneralInfoComponent),
      multi: true
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => IncidentGeneralInfoComponent),
      multi: true,
    }
  ],
  encapsulation: ViewEncapsulation.None,
})

export class IncidentGeneralInfoComponent implements OnInit, OnChanges, ControlValueAccessor {
  today = new Date();

  _data: CoreTypes.SimpleIncident = new CoreTypes.SimpleIncident();
  @Input('data')
  set data(data: CoreTypes.SimpleIncident) {
    this.writeValue(data);
  }
  get data() {
    return this._data;
  }
  @Input() readonly: boolean = false;
  @Input() incidentTypeFullList: CoreTypes.IncidentType[];
  @Input() incidentSubTypeFullList: CoreTypes.IncidentSubType[];
  @Input() incidentCategoryColorList: LabelValue<number>[] = [];
  @Input() incidentOriginFullList: CoreTypes.IncidentOrigin[] = [];

  @Input() displayTitle: boolean = false;

  @Input() incidentID: number = 0;
  @Input() communicationID: number = 0;
  @Input() isIncidentFile: boolean = false;

  @Output() onChangeCategory = new EventEmitter<boolean>();

  originalValue:  CoreTypes.SimpleIncident = new CoreTypes.SimpleIncident();

  isDisabled: boolean;
  onChange = (_:any) => { }
  onTouch = () => { }

  _displayControl: boolean = false;

  _incidentTypeList: LabelValue<number>[] = []
  _incidentSubTypeList: LabelValue<number>[] = [];
  _incidentChannelList: LabelValue<number>[] = [];
  
  _prioritiesFullList: CoreTypes.IncidentPriority[] = [];
  _incidentStatusList: LabelValue<number>[] = [];
  _incidentStatusFullList: CoreTypes.IncidentStatus[] = [];

  subscriptions: Subscription[] = [];

  form = this.fb.group({
    dateTimeCreated: ['', []],
    dateTimeClosed: ['', []],
    incidentType: ['', []],
    incidentSubType: ['', []],
    incidentCategoryColor: ['', []],
    incidentStatus: [''],
    incidentChannel: ['', []],
    reportingEntity: ['', []],
  });

  constructor(
    private fb: UntypedFormBuilder,
    public appService: AppService,
    private coreProxyService: CoreProxyService,
    private cdRef:ChangeDetectorRef
  ) { }

  registerOnChange(fn: any): void {
    this.onChange = () => {
      const formValue = this.form.getRawValue();
      const updatedValue = { ...this.originalValue, ...this._data };
  
      if (JSON.stringify(updatedValue) !== JSON.stringify(this.originalValue)) {
        this.originalValue = updatedValue;
        fn(updatedValue);
      }
    };
  }

  registerOnTouched(fn: any): void {
    this.onTouch = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
  }

  public validate() {
    let errors: ValidationErrors = {};
    Object.keys(this.form.controls).forEach(key => {
      //this.form.get(key).updateValueAndValidity();
      const controlErrors: ValidationErrors = this.form.get(key).errors;
      if (controlErrors != null) {
        errors[key] = controlErrors;
      }
    });
    return errors;
  }

  ngOnInit(): void {
    this.initData();
    this.subscriptions.push(this.incidentType.valueChanges.pipe(debounceTime(300)).subscribe(value => {
      if(value?.value || value?.value == 0) {
        this._data.TypeID = value.value;
        this.onInput();
      }
    }));
    this.subscriptions.push(this.incidentSubType.valueChanges.pipe(debounceTime(300)).subscribe(value => {
      if(value?.value || value?.value == 0) {
        this._data.SubTypeID = value.value;
        this.onInput();
      }
    }));
    this.subscriptions.push(this.incidentChannel.valueChanges.pipe(debounceTime(300)).subscribe(value => {
      if(value?.value || value?.value == 0) {
        this._data.IncidentOrigin = value.value;
        this.onInput();
      }
    }));
    this.subscriptions.push(this.incidentCategoryColor.valueChanges.pipe(debounceTime(300)).subscribe(value => {
      if(value?.value || value?.value == 0) {
        this._data.ColorID = value.value;
        this.onInput();
      }
    }));
    this.subscriptions.push(this.reportingEntity.valueChanges.pipe(debounceTime(300)).subscribe(value => {
      if(value) {
        this._data.ReportedBy = value;
        this.onInput();
      }
    }));
  }

  ngOnChanges() {
   this.initData();
  }

  initData() {
    this.getColorsAllowedForIncidents();
    this.getIncidentTypes();
    this.getIncidentOrigin();
  }

  onInput() {
    this.onTouch();
    this.onChange(this._data);
  }

  writeValue(value: any): void {
    if (value) {
      this.originalValue = JSON.parse(JSON.stringify(value));
      this.form.patchValue(this.originalValue, { emitEvent: false });
      this._data = value;
      this.dateTimeCreated.setValue(this._data.DateTimeCreated);
      this.dateTimeClosed.setValue(this._data.DateTimeClosed);
      const incidentType = this._data.TypeID ? this._incidentTypeList.find(x => x.value === this._data.TypeID) : this._incidentTypeList[0];
      this.incidentType.setValue(incidentType);
      const incidentSubType = this._data.SubTypeID ? this._incidentSubTypeList.find(x => x.value === this._data.SubTypeID) : this._incidentSubTypeList[0];
      this.incidentSubType.setValue(incidentSubType);
      let incidentCategory = this._data?.ColorID ? this.incidentCategoryColorList?.find(i => i.value === this._data?.ColorID) :
      this.incidentCategoryColorList?.find(i => i.value === this.appService.client?.Client?.IncidentSettings?.DefaultIncidentColorID)?? this.incidentCategoryColorList[0];
      this.incidentCategoryColor.setValue(incidentCategory);
      const incidentOrigin = this._data.IncidentOrigin ? this._incidentChannelList.find(x => x.value === this._data.IncidentOrigin) : this._incidentChannelList[0];
      this.incidentChannel.setValue(incidentOrigin);
      this.reportingEntity.setValue(this._data.ReportedBy);
    } else {
      this._data = new CoreTypes.SimpleIncident();
    }
    this.disabledData();
    this.onInput();
  }

  applyFilters(controlName:string) {
    this.today = new Date();
    if(controlName === "dateFrom") { this._data.DateTimeCreated = this.dateTimeCreated.value; }
    if(controlName === "dateTo") { this._data.DateTimeClosed = this.dateTimeClosed.value; }
  }

  onChangeCategoryColor() { 
    this.onChangeCategory.emit(true);
  }

  disabledData() {
    if(this._data?.DateTimeClosed?.Date?.Day) {
      this.incidentCategoryColor.disable();
      this.incidentSubType.disable();
      this.incidentType.disable();
      this.dateTimeClosed.disable();
    } else {
      this.incidentCategoryColor.enable();
      this.incidentSubType.enable();
      this.incidentType.enable();
      this.dateTimeClosed.enable();
      
    }
    if(this._data?.DateTimeClosed?.Date?.Day || this.incidentID) {
      this.dateTimeCreated.disable();
    }
  }

  async getIncidentTypes() {
    if(this.incidentTypeFullList?.length) {
      let items: LabelValue<number>[] = [];
      this.incidentTypeFullList.forEach((i) => {
        const item = {
          label: i.Description,
          value: i.IncidentTypeID,
        };
        items.push(item);
      });
      this._incidentTypeList = items;
      const incidentType = this._data?.TypeID ? this._incidentTypeList.find(x => x.value === this._data?.TypeID) : this._incidentTypeList[0];
      this.incidentType.setValue(incidentType);
      this.getIncidentSubTypes();
    }
  }

  getIncidentSubTypes() {
    if(this.incidentSubTypeFullList?.length){
      let items: LabelValue<number>[] = [];
      this.incidentSubTypeFullList.forEach((i) => {
        const item = {
          label: i.Description,
          value: i.IncidentSubTypeID,
        };
        if (this._data?.SubTypeID === i.IncidentSubTypeID || items.length === 0) {
          this.incidentSubType.setValue(item);
          this.cdRef.detectChanges();
    }
        items.push(item);
      });
      this._incidentSubTypeList = items;
      const incidentSubType = this._data?.SubTypeID ? this._incidentSubTypeList.find(x => x.value === this._data?.SubTypeID) : this._incidentSubTypeList[0];
      this.incidentSubType.setValue(incidentSubType);
      this.cdRef.detectChanges();
      this.onInput();
    }
  }

  async getColorsAllowedForIncidents() {
    if(!this.incidentCategoryColorList?.length) return
    let findColor = this.incidentID ? this.incidentCategoryColorList?.find(i => i.value === this._data?.ColorID) :
    this.incidentCategoryColorList?.find(i => i.value === this.appService.client?.Client?.IncidentSettings?.DefaultIncidentColorID)?? this.incidentCategoryColorList[0];
    this.incidentCategoryColor.setValue(findColor);
    this.onInput();
  }

  async getIncidentOrigin() {
    if(!this.incidentOriginFullList?.length) return;
    let items: LabelValue<number>[] = [];
    this.incidentOriginFullList.forEach((i) => {
      const item = {
        label: i.IncidentOriginName,
        value: i.IncidentOriginID,
      };
      items.push(item);
    });
    this._incidentChannelList = items;
    const incidentChannel = this._data.IncidentOrigin ? this._incidentChannelList.find(x => x.value === this._data.IncidentOrigin ) : this._incidentChannelList[0];
    this.incidentChannel.setValue(incidentChannel);
    this.onInput();
  }
  
  onIncidentSubTypes(incidentType) {
    if(!incidentType) return;
    this._incidentSubTypeList = [];
    this.incidentSubType.setValue(null);
    this.cdRef.detectChanges();
    let items: LabelValue<number>[] = [];
    this.coreProxyService.IncidentService.EnumIncidentSubTypes(
      this.appService.getAccessToken(),
      this.appService.getFacilityID(),
      incidentType,
      (data:CoreTypes.IncidentSubType[]) => {
        this.incidentSubTypeFullList = data;
        data.forEach((i) => {
          const item = {
            label: i.Description ? i.Description : i.SubtypeName,
            value: i.IncidentSubTypeID,
          };
          if (this._data?.SubTypeID === i.IncidentSubTypeID || items.length === 0) {
            this.incidentSubType.setValue(item);
            this.cdRef.detectChanges();
          }
          items.push(item);
        });
        setTimeout(() => {
          this._incidentSubTypeList = [...items];
          const incidentSubType = this._incidentSubTypeList.find(i => i.value === this._data.SubTypeID)?? this._incidentSubTypeList[0];
          this.incidentSubType.setValue(incidentSubType);
          this.cdRef.detectChanges();
        }, 50);
      },
      err => {
        this.appService.showException(err);
      }
    );
  }

  get dateTimeCreated() { return this.form.get('dateTimeCreated'); }
  get dateTimeClosed() { return this.form.get('dateTimeClosed'); }
  get incidentType() { return this.form.get('incidentType'); }
  get incidentSubType() { return this.form.get('incidentSubType'); }
  get incidentCategoryColor() { return this.form.get('incidentCategoryColor'); }
  get incidentChannel() { return this.form.get('incidentChannel'); }
  get incidentStatus() { return this.form.get('incidentStatus'); }
  get reportingEntity() { return this.form.get('reportingEntity'); }
}
